import type { DataBaseline } from "@/store/slices/session.slice";
import type { ParticipantSurveyDraft } from "@/store/slices/participantFlow.slice";

export const LOCAL_MP3_TEST_SESSION_STORAGE_KEY =
  "imags_local_mp3_test_session_v1";
export const LOCAL_MP3_TEST_SESSION_MAX_SAMPLES = 10_000;

export type LocalMp3TestTrack = {
  id: string;
  fileName: string;
  title: string;
  recognizedTitle?: string | null;
  artist?: string | null;
  manualTitle?: string | null;
  manualArtist?: string | null;
  durationMs: number | null;
};

export type LocalMp3TestSample = {
  timestamp: number;
  elapsedMs: number;
  gsrRawAdc: number | null;
  gsrSmoothAdc: number | null;
  gsrUiLevel: number | null;
  trackId: string | null;
  trackPositionMs: number | null;
  isPlaying: boolean;
};

export type LocalMp3TestSession = {
  version: 1;
  id: string;
  startedAt: number;
  finishedAt: number | null;
  lastSampleAt: number | null;
  baseline: DataBaseline | null;
  preSurvey: ParticipantSurveyDraft;
  tracks: LocalMp3TestTrack[];
  samples: LocalMp3TestSample[];
  droppedSampleCount: number;
};

function createSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `local-mp3-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createLocalMp3TestSession({
  baseline,
  preSurvey,
}: {
  baseline: DataBaseline | null;
  preSurvey: ParticipantSurveyDraft;
}): LocalMp3TestSession {
  return {
    version: 1,
    id: createSessionId(),
    startedAt: Date.now(),
    finishedAt: null,
    lastSampleAt: null,
    baseline: baseline ? { ...baseline } : null,
    preSurvey: { ...preSurvey },
    tracks: [],
    samples: [],
    droppedSampleCount: 0,
  };
}

export function saveLocalMp3TestSession(
  session: LocalMp3TestSession,
): string | null {
  if (typeof window === "undefined") {
    return "Browser storage is unavailable.";
  }

  try {
    window.localStorage.setItem(
      LOCAL_MP3_TEST_SESSION_STORAGE_KEY,
      JSON.stringify(session),
    );
    return null;
  } catch (error) {
    console.error("[LocalMp3TestSession] Failed to save session", error);
    return "The test session could not be saved in browser storage.";
  }
}

export function readLocalMp3TestSession(): LocalMp3TestSession | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(
      LOCAL_MP3_TEST_SESSION_STORAGE_KEY,
    );
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LocalMp3TestSession>;
    if (
      parsed.version !== 1 ||
      typeof parsed.id !== "string" ||
      typeof parsed.startedAt !== "number" ||
      !Array.isArray(parsed.samples) ||
      !Array.isArray(parsed.tracks)
    ) {
      return null;
    }

    return parsed as LocalMp3TestSession;
  } catch (error) {
    console.error("[LocalMp3TestSession] Failed to read session", error);
    return null;
  }
}
